import {useEffect, useRef} from "react";
import {useTeamProgressContext} from "../context/TeamProgressContext";
import {
	useExportTeamProgressContext,
	type ExportReportType,
} from "../context/ExportTeamProgressContext";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {useWebSocket} from "@/app2/shared/contexts/WebSocketContext";
import {useStartTeamProgressExport, useDownloadCsvFromBlob} from "@/app2/shared/data/repos/team-progress";
import fileDownload from "js-file-download";
import dayjs from "dayjs";
import type {GridFilterModel, GridSortModel} from "@mui/x-data-grid-pro";
import {gridFilterModelToApiFilters} from "../utils/filters";

/**
 * Filter interface for data grid
 */
interface IEntityFilter<T> {
	field: keyof T;
	operator: string;
	value: any;
}

/**
 * Hook to manage Team Progress report exports
 * 
 * Handles:
 * - Export job initiation via API
 * - WebSocket listener for job completion
 * - CSV download from blob storage
 * - Export state management (one at a time policy)
 * - User notifications (progress, success, error)
 */
export function useManageTeamProgressReport<T>(reportType: ExportReportType, filterModel: GridFilterModel, sortModel: GridSortModel) {
	const {t} = useSSTranslation();
	const {socket} = useWebSocket();
	const {filters} = useTeamProgressContext();
	const {addExportTask, exportTaskExists, exportTasksRunning, removeExportTask} =
		useExportTeamProgressContext();
	const {errorSnackbar, processInProgressSnackbar} =
		useCustomSnackbar();

	// React Query mutation for starting export
	const {mutate: startExport, isPending} = useStartTeamProgressExport();

	/**
	 * Initiate export download
	 */
	const initDownload = () => {
		// Check if export already in progress for this report type
		if (exportTaskExists(reportType)) {
			return;
		}

		// Check if any other export is running (one at a time policy)
		if (exportTasksRunning()) {
			errorSnackbar(
				t("team_progress_one_report_a_time_msg", "You can only download one report at a time"),
				true
			);
			return;
		}

		// Check WebSocket connection
		if (!socket) {
			errorSnackbar(t("team_progress_download_export_failed", "Export failed. Please try again."));
			return;
		}

		// Add export task to state
		addExportTask(reportType);

		// Call API to start export job
		startExport(
			{
				reportType,
				archivedUsers: filters.archivedUsers,
				dateFrom: dayjs(filters.dateFrom).format("YYYY-MM-DD"),
				dateTo: dayjs(filters.dateTo).format("YYYY-MM-DD"),
				filters: gridFilterModelToApiFilters(filterModel),
				order: sortModel[0]?.sort || "desc",
				orderBy: sortModel[0]?.field || "completedOn"
			},
			{
				onSuccess: (data) => {
					processInProgressSnackbar(
						t(
							"team_progress_generating_your_report_msg",
							"Generating your report… Don't close or refresh this window."
						),
						true,
						true
					);
				},
				onError: (error) => {
					removeExportTask(reportType);
					errorSnackbar(
						t("team_progress_download_export_failed", "Export failed. Please try again.")
					);
				},
			}
		);
	};

	return {
		initDownload,
		isExporting: exportTaskExists(reportType),
	};
}

/**
 * Hook to handle export completion WebSocket event and CSV download
 * 
 * Listens for 'sendTeamProgressDataForReports' event from backend
 * and downloads the CSV file from blob storage.
 */
export function useDownloadReport() {
	const {t} = useSSTranslation();
	const {socket} = useWebSocket();
	const {successSnackbar, errorSnackbar, closeSnackbar} = useCustomSnackbar();
	const {removeExportTask} = useExportTeamProgressContext();
	const fileNameRef = useRef<string | null>(null);
	const reportTypeRef = useRef<string | null>(null);

	// React Query mutation for downloading CSV
	const {mutate: downloadCsv, isPending: isDownloading} = useDownloadCsvFromBlob();

	// Handle CSV download completion
	useEffect(() => {
		if (!isDownloading && fileNameRef.current) {
			// Download will be handled by mutation onSuccess
			fileNameRef.current = null;
			reportTypeRef.current = null;
		}
	}, [isDownloading]);

	// Listen for WebSocket export completion event
	useEffect(() => {
		if (!socket) return;

		const handleExportComplete = (eventData: {
			isSuccessful: boolean;
			fileName: string;
			reportType: string;
		}) => {
			const {isSuccessful, fileName, reportType} = eventData;

			// Remove export task from state
			removeExportTask(reportType as ExportReportType);

			if (!isSuccessful) {
				closeSnackbar();
				errorSnackbar(
					t("team_progress_download_export_failed", "Export failed. Please try again.")
				);
				return;
			}

			// Store file name and initiate download
			fileNameRef.current = fileName;
			reportTypeRef.current = reportType;

			downloadCsv(
				{
					containerName: "api-image-storage", // Default Azure container for exports
					blobName: fileName,
					mimeType: "text/csv",
				},
				{
					onSuccess: (blob) => {
						// Extract just the filename without the folder path and GUID
						const displayName = fileName.split('/').pop() || fileName;
						fileDownload(blob, displayName);
						closeSnackbar();
						successSnackbar(
							t("team_progress_download_report_has_started", "Your download has started")
						);
					},
					onError: () => {
						closeSnackbar();
						errorSnackbar(
							t("team_progress_download_export_failed", "Export failed. Please try again.")
						);
					},
				}
			);
		};

		socket.on("sendTeamProgressDataForReports", handleExportComplete);

		return () => {
			socket.off("sendTeamProgressDataForReports", handleExportComplete);
		};
	}, [socket, t, successSnackbar, errorSnackbar, closeSnackbar, downloadCsv, removeExportTask]);

	return {
		isDownloading,
	};
}
